<template>
  <div class="flex flex-col gap-4">
    <div class="flex flex-wrap items-end gap-2">
      <div class="flex-1 flex gap-4">
        <BaseInput
          id="latitude"
          v-model.number="lat"
          :label="$t('inputs.location.lat')"
          type="number"
          step="any"
          placeholder="4.31375500"
          :error="errorMessage"
          @update:model-value="!isSelecting && emits('updateCoords')"
          width="w-full"
        />

        <BaseInput
          id="longitude"
          v-model.number="lng"
          :label="$t('inputs.location.lng')"
          type="number"
          step="any"
          placeholder="12.1018020"
          :error="errorMessage"
          @update:model-value="!isSelecting && emits('updateCoords')"
          width="w-full"
        />
      </div>

      <button
        type="button"
        @click="isSelecting = !isSelecting"
        class="flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-main hover:text-main-hover hover:bg-blue-50 rounded-md transition-colors"
      >
        <Icon icon="mdi:crosshairs-gps" class="w-5 h-5" />
        {{ $t('inputs.coordsSelector.buttons.select') }}
      </button>
    </div>

    <div class="relative h-60 rounded-lg shadow-md overflow-hidden">
      <Map ref="coords-map" :small="true" map-id="coords-map" class="w-full h-full">
        <div
          v-if="isSelecting"
          class="absolute top-2 left-0 right-0 z-10 text-center pointer-events-none"
        >
          <span
            class="inline-block px-4 py-2 bg-white/90 backdrop-blur-sm text-xs font-medium text-gray-700 rounded-full shadow-md"
          >
            {{ $t('inputs.coordsSelector.moveMap') }}
          </span>
        </div>

        <div
          v-if="isSelecting"
          class="absolute inset-0 z-10 flex items-center justify-center pointer-events-none"
        >
          <Icon icon="mdi:crosshairs-gps" class="w-6 h-6 text-main" />
        </div>

        <div
          v-if="isSelecting"
          class="absolute bottom-3 left-0 right-0 z-10 flex items-center justify-center gap-2 px-4"
        >
          <button
            type="button"
            @click="validate"
            class="inline-flex items-center gap-2 px-4 py-2 bg-main hover:bg-main-hover text-white text-sm font-medium rounded-md shadow-lg transition-colors"
          >
            <Icon icon="heroicons:check-20-solid" class="w-4 h-4" />
            {{ $t('inputs.coordsSelector.buttons.validate') }}
          </button>

          <button
            type="button"
            @click="cancel"
            class="inline-flex items-center gap-2 px-4 py-2 bg-white hover:bg-gray-50 text-gray-700 text-sm font-medium rounded-md shadow-lg transition-colors"
          >
            <Icon icon="heroicons:x-mark-20-solid" class="w-4 h-4" />
            {{ $t('inputs.coordsSelector.buttons.cancel') }}
          </button>
        </div>
      </Map>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref, useTemplateRef, watch, type ModelRef } from 'vue'
import { Icon } from '@iconify/vue'
import BaseInput from '@/components/ui/BaseInput.vue'
import Map from '../map/Map.vue'
import coordsIcon from '@/assets/images/icons/map/project_icon.png'
import MapService from '@/services/map/MapService'

const lat: ModelRef<number | undefined> = defineModel('lat')
const lng: ModelRef<number | undefined> = defineModel('lng')

defineProps<{ errorMessage?: string }>()
const emits = defineEmits(['updateCoords'])

type MapType = InstanceType<typeof Map>
const coordsMap = useTemplateRef<MapType>('coords-map')

const isSelecting = ref(false)
const savedCoords = ref({ lat: lat.value, lng: lng.value })

const sources = { coords: 'coords' }
const coordsSourceName = sources.coords
const coordsLayerName = sources.coords
const coordsImageName = sources.coords

const map = computed(() => coordsMap.value?.map)

const coords = computed(() => {
  if (lat.value == null || lng.value == null) return
  return { lat: lat.value, lng: lng.value }
})

const geojson = computed(() => MapService.getGeojson([{ geoData: { coords: coords.value } }]))

// Initialization Logic
watch(
  () => map.value,
  (newMap) => {
    if (!newMap) return

    const init = () => initSearchedPin()

    if (newMap.loaded()) {
      init()
    } else {
      newMap.on('load', init)
    }

    newMap.on('moveend', () => {
      if (!isSelecting.value) return
      const center = newMap.getCenter()
      lat.value = center?.lat
      lng.value = center?.lng
    })
  },
  { immediate: true },
)

// Logic Handlers
watch(isSelecting, (newValue) => {
  if (newValue) {
    savedCoords.value = { lat: lat.value, lng: lng.value }
  }
})

watch(
  () => [lat.value, lng.value],
  () => {
    if (!isSelecting.value) {
      refreshCoordsLayer()
    }
  },
)

const cancel = () => {
  isSelecting.value = false
  lat.value = savedCoords.value.lat
  lng.value = savedCoords.value.lng
}

const validate = () => {
  isSelecting.value = false
  refreshCoordsLayer()
  emits('updateCoords')
}

// Map Layers
const initSearchedPin = () => {
  setSearchedPinLayer()
}

const setSearchedPinLayer = async () => {
  if (!coordsMap.value) return

  coordsMap.value.addSource(coordsSourceName, geojson.value)
  await coordsMap.value.addImage(coordsIcon, coordsImageName)

  const layout: maplibregl.LayerSpecification['layout'] = {
    'icon-image': coordsImageName,
    'icon-size': 0.35,
  }

  coordsMap.value.addLayer(coordsLayerName, { layout })
  flyToCoords()
}

const refreshCoordsLayer = async () => {
  if (coordsMap.value) {
    coordsMap.value.setData(sources.coords, geojson.value)
    flyToCoords()
  }
}

const flyToCoords = () => {
  if (coordsMap.value && coords.value) {
    coordsMap.value.flyTo(coords.value, 1)
  }
}
</script>
